import { makeStyles, useTheme } from '@mui/styles'
import React, { useState } from 'react'
import classNames from 'classnames'

import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import { useAppSelector } from '@/redux/store'
import SignaturePenIcon from '@assets/images/icons/signature_pen.svg'
import IconCheckCircle from '@assets/images/icons/check_circle.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ThemeInterface from '@/theme-interface'
import { getContrastText } from '@/modules/base/helpers/getContrastText'
import lang from '@/lang'
import MarkerBase from '@/modules/signature/components/overlay/marker-base'

type SignaturePositionMarkerProps = {
	active: boolean,
	i: number,
	signature: {
		name: string,
		signerEmail?: string,
		signed?: boolean,
		position: {
			x: number,
			y: number,
			page: number,
		},
		color?: string,
		signature?: string,
	},
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  marker: {
    '&$signing': {
      padding: 0,
    },
  },
  signing: {
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  signature: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    '& > img': {
      width: '100%',
      height: '100%',
    },
  },
  checkIcon: {
    position: 'absolute',
    right: 0,
    transform: 'translate(calc(100% + 5px))',
  },
}), { name: 'SignaturePositionMarker' })

const SignaturePositionMarker = (props: SignaturePositionMarkerProps) => {
  const { signature, active, i } = props
  const theme: ThemeInterface = useTheme()

  const userEmail = useAppSelector(state => state.user.info.email)
  const isTaxConsultant = useAppSelector(state => state.tenant.info.isTaxConsultant)
  const {
    currentSignerEmail,
    openSigningModal,
    signingMode,
    pageScaleFactor,
  } = useSignatureContext()
  const { position, signature: signing, signerEmail, signed, color: signatureColor } = signature
  const [isDragging, setIsDragging] = useState(false)

  const classes = useStyles({ active, isDragging, signing, signed, signingMode, signatureColor, pageScaleFactor })

  const handleSignatureMarkerClick = () => {
    if (!signingMode || signed || (signerEmail !== userEmail && !isTaxConsultant)) { return }

    openSigningModal(i)
  }

  return (
    <MarkerBase
      i={i}
      active={active}
      position={position}
      signed={signed}
      signatureColor={signatureColor}
      innerClassName={classNames(classes.marker, signing && classes.signing)}
      styleConfig={{ scaled: true, aspectRatio: 3 }}
      onDragStart={() => setIsDragging(true)}
      onDragStop={() => setIsDragging(false)}
      onClick={handleSignatureMarkerClick} >
      <>
      	{!signing && (
          <>
            <SVGIcon
              svg={SignaturePenIcon}
              height="13px"
              width="13px"
              fill={!signing && !signed ? getContrastText(signatureColor) : 'transparent'} />
            <div className={classes.content}>
              <span>{lang.signature.signature}</span>
            </div>
          </>
        )}
        {signing && (
          <div className={classes.signature}>
            <img src={signing} />
            <SVGIcon className={classes.checkIcon} svg={IconCheckCircle} height="16px" width="16px" fill={theme.green} />
          </div>
        )}
      </>
    </MarkerBase>
  )
}

export default SignaturePositionMarker
